import { AxiosResponse } from "axios";
import { call, fork, put, takeLatest } from "redux-saga/effects";

import { ModalType } from "entities/common";
import { CreateGatewayRequest, GatewayItem, GatewayStatistics } from "entities/gateways";
import { GatewaysApi } from "services/http";
import { getFetchSaga } from "store/commonSagas";
import { hideModal, showModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";
import { getApiErrorMessage, isFieldLevelError } from "utils/helpers";
import {
  UpdateGatewayPayload,
  createGatewayFailure,
  createGatewayRequest,
  createGatewaySuccess,
  getGatewayFailure,
  getGatewayListFailure,
  getGatewayListRequest,
  getGatewayListSuccess,
  getGatewayRequest,
  getGatewayStatisticsFailure,
  getGatewayStatisticsRequest,
  getGatewayStatisticsSuccess,
  getGatewaySuccess,
  removeGatewayFailure,
  removeGatewayRequest,
  removeGatewaySuccess,
  updateGatewayFailure,
  updateGatewayRequest,
  updateGatewaySuccess,
} from "store/slices/gatewaySlice";

const watchGetList = getFetchSaga(
  getGatewayListRequest.type,
  GatewaysApi.getList,
  getGatewayListSuccess,
  getGatewayListFailure,
);

const watchGetCurrent = getFetchSaga(
  getGatewayRequest.type,
  GatewaysApi.getCurrent,
  getGatewaySuccess,
  getGatewayFailure,
);

function* createGatewayFlow(action: { type: string; payload: CreateGatewayRequest }) {
  try {
    const response: AxiosResponse<GatewayItem> = yield call(GatewaysApi.create, action.payload);
    const gateway = response.data;
    yield put(createGatewaySuccess(gateway));

    yield put(
      showToast({
        id: `gateway-created-${gateway.id}`,
        message: "The gateway was successfully added.",
        type: "success",
      }),
    );
    yield put(
      showModal({
        modalType: ModalType.GATEWAY_CONFIG_INSTRUCTIONS,
        modalProps: {
          gatewayName: gateway.name,
          gatewayEui: gateway.gateway_eui,
        },
      }),
    );
  } catch (error) {
    yield put(createGatewayFailure(error as { detail: Record<string, string> }));
    if (!isFieldLevelError(error)) {
      yield put(
        showToast({
          id: `gateway-create-failed-${Date.now()}`,
          message: getApiErrorMessage(error, "Failed to add gateway. Please try again later."),
          type: "error",
        }),
      );
    }
  }
}

function* watchCreate() {
  yield takeLatest(createGatewayRequest.type, createGatewayFlow);
}

function* updateGatewayFlow(action: { type: string; payload: UpdateGatewayPayload }) {
  const { id, body } = action.payload;
  try {
    const response: AxiosResponse<GatewayItem> = yield call(GatewaysApi.update, id, body);
    const gateway = response.data;
    yield put(updateGatewaySuccess(gateway));

    yield put(
      showToast({
        id: `gateway-updated-${gateway.id}`,
        message: "The gateway was successfully updated.",
        type: "success",
      }),
    );
  } catch (error) {
    yield put(updateGatewayFailure(error as { detail: Record<string, string> }));
    if (!isFieldLevelError(error)) {
      yield put(
        showToast({
          id: `gateway-update-failed-${Date.now()}`,
          message: getApiErrorMessage(error, "Failed to update gateway. Please try again later."),
          type: "error",
        }),
      );
    }
  }
}

function* watchUpdate() {
  yield takeLatest(updateGatewayRequest.type, updateGatewayFlow);
}

function* removeGatewayFlow(action: { type: string; payload: number }) {
  const id = action.payload;
  try {
    yield call(GatewaysApi.remove, id);
    yield put(removeGatewaySuccess(id));
    yield put(hideModal());
    yield put(
      showToast({
        id: `gateway-removed-${id}`,
        message: "The gateway was successfully deleted.",
        type: "success",
      }),
    );
  } catch (error) {
    yield put(removeGatewayFailure(error as { detail: Record<string, string> }));
    yield put(
      showToast({
        id: `gateway-remove-failed-${Date.now()}`,
        message: getApiErrorMessage(error, "Failed to delete gateway. Please try again later."),
        type: "error",
      }),
    );
  }
}

function* watchRemove() {
  yield takeLatest(removeGatewayRequest.type, removeGatewayFlow);
}

function* getStatisticsFlow() {
  try {
    const response: AxiosResponse<GatewayStatistics> = yield call(GatewaysApi.getStatistics);
    yield put(getGatewayStatisticsSuccess(response.data));
  } catch {
    yield put(getGatewayStatisticsFailure());
  }
}

function* watchGetStatistics() {
  yield takeLatest(getGatewayStatisticsRequest.type, getStatisticsFlow);
}

export const gatewayWatchers = [
  fork(watchGetList),
  fork(watchGetCurrent),
  fork(watchCreate),
  fork(watchUpdate),
  fork(watchRemove),
  fork(watchGetStatistics),
];
